import { Building2, Flame, Heart, Users, MapPinned, Warehouse } from "lucide-react";

interface OperationsStatusWidgetProps {
  orgName: string | null;
  orgTier: number | null;
  orgMorale: number | null;
  orgHeat: number | null;
  orgMembers: Record<string, any> | any[] | null;
  controlledCount: number | null;
  contestedCount: number | null;
  resourcePools: Record<string, any> | null;
  compact?: boolean;
}

export function OperationsStatusWidget({
  orgName, orgTier, orgMorale, orgHeat, orgMembers,
  controlledCount, contestedCount, resourcePools, compact,
}: OperationsStatusWidgetProps) {
  const tier = orgTier ?? 0;
  const morale = orgMorale ?? 50;
  const heat = orgHeat ?? 0;
  const ctrl = controlledCount ?? 0;
  const cont = contestedCount ?? 0;

  let activeMembers = 0;
  if (orgMembers && typeof orgMembers === "object" && !Array.isArray(orgMembers)) {
    activeMembers = Object.values(orgMembers).reduce((s: number, b: any) => s + (b?.status === "active" ? (b.count || 0) : 0), 0);
  } else if (Array.isArray(orgMembers)) {
    activeMembers = orgMembers.filter((m: any) => m.status === "active").length;
  }

  const resCount = resourcePools ? Object.keys(resourcePools).length : 0;

  if (compact) {
    return (
      <div className="flex items-center gap-1.5 text-muted-foreground">
        {tier > 0 && <><Building2 className="w-3 h-3" /><span className="text-[10px]">T{tier}</span></>}
        {activeMembers > 0 && <><Users className="w-2.5 h-2.5" /><span className="text-[10px]">{activeMembers}</span></>}
        {ctrl > 0 && <><MapPinned className="w-2.5 h-2.5" /><span className="text-[10px]">{ctrl}</span></>}
        {cont > 0 && <span className="text-[10px] text-destructive">⚔️{cont}</span>}
        <Heart className={`w-2.5 h-2.5 ${morale < 40 ? "text-destructive" : "text-green-400"}`} />
        <span className="text-[10px]">{morale}</span>
        {heat > 20 && <><Flame className={`w-2.5 h-2.5 ${heat > 60 ? "text-destructive" : "text-orange-400"}`} /><span className="text-[10px]">{heat}</span></>}
        {resCount > 0 && <><Warehouse className="w-2.5 h-2.5" /><span className="text-[10px]">{resCount}</span></>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">{orgName || "Operations"}</span>
        {tier > 0 && <span className="text-[10px] text-muted-foreground">Tier {tier}</span>}
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
        {activeMembers > 0 && (
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{activeMembers}</span>
        )}
        {ctrl > 0 && (
          <span className="flex items-center gap-1"><MapPinned className="w-3 h-3" />{ctrl} territories</span>
        )}
        {cont > 0 && (
          <span className="flex items-center gap-1 text-destructive">⚔️ {cont} contested</span>
        )}
        <span className="flex items-center gap-1">
          <Heart className={`w-3 h-3 ${morale < 40 ? "text-destructive" : "text-green-400"}`} />
          {morale}
        </span>
        {heat > 0 && (
          <span className="flex items-center gap-1">
            <Flame className={`w-3 h-3 ${heat > 60 ? "text-destructive" : "text-orange-400"}`} />
            {heat}
          </span>
        )}
      </div>
    </div>
  );
}
